import React, { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import api from "@/lib/api";

import {
  setupNotificationAudioUnlock,
  setFaviconBadge,
  updateDocumentTitle,
  notifyNewNotification,
} from "@/lib/notificationAlerts";

import { NewTaskDialog } from "@/components/NewTaskDialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import {
  LayoutDashboard,
  CalendarDays,
  CalendarClock,
  Users,
  Building2,
  BarChart3,
  Bell,
  LogOut,
  Home,
  Plus,
  CheckSquare,
} from "lucide-react";


const ADMIN_NAV = [
  {
    to: "/dashboard",
    label: "Dashboard",
    icon: LayoutDashboard,
  },
  {
    to: "/tareas",
    label: "Tareas",
    icon: CalendarDays,
  },
  {
    to: "/reuniones",
    label: "Reuniones",
    icon: CalendarClock,
  },
  {
    to: "/funcionarios",
    label: "Funcionarios",
    icon: Users,
  },
  {
    to: "/areas",
    label: "Áreas",
    icon: Building2,
  },
  {
    to: "/cumplimiento",
    label: "Cumplimiento",
    icon: BarChart3,
  },
  {
    to: "/notificaciones",
    label: "Notificaciones",
    icon: Bell,
  },
];


const EMP_NAV = [
  {
    to: "/hoy",
    label: "Hoy",
    icon: Home,
  },
  {
    to: "/tareas",
    label: "Calendario",
    icon: CalendarDays,
  },
  {
    to: "/reuniones",
    label: "Reuniones",
    icon: CalendarClock,
  },
  {
    to: "/mi-cumplimiento",
    label: "Cumplimiento",
    icon: BarChart3,
  },
  {
    to: "/notificaciones",
    label: "Alertas",
    icon: Bell,
  },
];


function Logo({ small }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-900 text-white">
        <CheckSquare className="h-5 w-5" />
      </div>

      {!small && (
        <div className="leading-tight">
          <div className="font-heading text-base font-extrabold tracking-tight text-slate-900">
            Calendario VH
          </div>

          <div className="text-[10px] font-medium text-slate-400">
            Control operativo
          </div>
        </div>
      )}
    </div>
  );
}


export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [newTaskOpen, setNewTaskOpen] = useState(false);

  const isAdmin = user?.role === "admin";
  const isEmployee = user?.role === "empleado";
  const nav = isAdmin ? ADMIN_NAV : EMP_NAV;


  /*
   * ============================================================
   * NOTIFICACIONES
   * ============================================================
   */

  const { data: notif } = useQuery({
    queryKey: ["notifications"],

    queryFn: () =>
      api
        .get("/notifications")
        .then((r) => r.data),

    refetchInterval: 5000,

    refetchIntervalInBackground: true,
  });


  const unread = notif?.unread || 0;


  /*
   * Guardamos la última notificación conocida.
   */
  const previousUnread = useRef(null);


  /*
   * Evita que la primera carga produzca una alarma.
   */
  


  /*
   * ============================================================
   * DESBLOQUEO DEL AUDIO
   * ============================================================
   */

  useEffect(() => {
    const cleanupAudio = setupNotificationAudioUnlock();

    return cleanupAudio;
  }, []);


  /*
   * ============================================================
   * FAVICON + TÍTULO
   * ============================================================
   */

  useEffect(() => {
    updateDocumentTitle(unread);
    setFaviconBadge(unread);
  }, [unread]);


  /*
   * ============================================================
   * LISTENER PARA NOTIFICACIONES
   *
   * IMPORTANTE:
   * Este useEffect está al mismo nivel que los demás Hooks.
   * NO está dentro del useEffect de las notificaciones.
   * ============================================================
   */

  useEffect(() => {
    const handler = (event) => {
      const notification = event.detail || {};

      toast(
        notification.title || "Nueva notificación",
        {
          description: notification.message || "",
          duration: 6000,

          action: {
            label: "Ver",

            onClick: () => {
              navigate("/notificaciones");
            },
          },
        }
      );
    };


    window.addEventListener(
      "calendario:new-notification",
      handler
    );


    return () => {
      window.removeEventListener(
        "calendario:new-notification",
        handler
      );
    };
  }, [navigate]);


  /*
   * ============================================================
   * DETECTAR NUEVAS NOTIFICACIONES
   * ============================================================
   */

  useEffect(() => {
    if (!notif) {
      return;
    }

    const currentUnread = Number(notif.unread || 0);

    /*
     * Primera carga:
     * no emitir alarma por notificaciones existentes.
     */
    if (previousUnread.current === null) {
      previousUnread.current = currentUnread;

      console.log(
        "Calendario VH: unread inicial =",
        currentUnread
      );

      return;
    }

    /*
     * Si aumenta unread, acaba de llegar una notificación.
     */
    if (currentUnread > previousUnread.current) {
      console.log(
        "🚨 Calendario VH: NUEVA NOTIFICACIÓN DETECTADA",
        {
          anterior: previousUnread.current,
          actual: currentUnread,
        }
      );

      const newest =
        Array.isArray(notif.notifications) &&
        notif.notifications.length > 0
          ? notif.notifications[0]
          : {
              id: `notification-${Date.now()}`,
              title: "Nueva notificación",
              message: "Tienes una nueva notificación.",
            };

      notifyNewNotification(newest);
    }

    previousUnread.current = currentUnread;

  }, [notif]);


  /*
   * ============================================================
   * RENDER
   * ============================================================
   */

  return (
    <div className="min-h-screen bg-slate-50">

      {/* ======================================================
          DESKTOP SIDEBAR
          ====================================================== */}

      <aside className="fixed left-0 top-0 z-30 hidden h-screen w-64 flex-col border-r border-slate-200 bg-white px-4 py-6 md:flex">

        <div className="px-2">
          <Logo />
        </div>


        <nav className="mt-8 flex-1 space-y-1">

          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.to.replace("/", "")}`}

              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-all",

                  isActive
                    ? "bg-slate-900 text-white shadow-sm"
                    : "text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                )
              }
            >

              <item.icon className="h-[18px] w-[18px]" />

              {item.label}


              {item.to === "/notificaciones" && unread > 0 && (
                <span className="ml-auto rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white">
                  {unread}
                </span>
              )}

            </NavLink>
          ))}

        </nav>


        {/* Nueva tarea */}

        {(isAdmin || isEmployee) && (
          <Button
            onClick={() => setNewTaskOpen(true)}
            data-testid="sidebar-new-task-btn"

            className="mb-3 w-full rounded-full bg-emerald-500 font-bold hover:bg-emerald-600 active:scale-95"
          >
            <Plus className="mr-1 h-4 w-4" />

            Nueva Tarea
          </Button>
        )}


        {/* Usuario */}

        <div className="rounded-xl border border-slate-200 p-3">

          <div className="flex items-center gap-2">

            <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-slate-200 text-xs font-bold text-slate-600">

              {user?.avatar ? (
                <img
                  src={user.avatar}
                  alt=""
                  className="h-full w-full object-cover"
                />
              ) : (
                user?.name?.[0]
              )}

            </div>


            <div className="min-w-0 flex-1">

              <div className="truncate text-xs font-bold text-slate-900">
                {user?.name}
              </div>

              <div className="truncate text-[10px] text-slate-400">
                {isAdmin
                  ? "Administrador"
                  : "Funcionario"}
              </div>

            </div>


            <button
              onClick={logout}
              data-testid="logout-btn"
              className="text-slate-400 hover:text-red-500"
            >
              <LogOut className="h-4 w-4" />
            </button>

          </div>

        </div>

      </aside>


      {/* ======================================================
          MOBILE TOP HEADER
          ====================================================== */}

      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-slate-200 bg-white/80 px-4 py-3 backdrop-blur-xl md:hidden">

        <Logo small />

        <span className="font-heading text-sm font-extrabold text-slate-900">
          Calendario VH
        </span>


        <button
          onClick={() =>
            navigate("/notificaciones")
          }

          className="relative text-slate-600"

          data-testid="mobile-notif-btn"
        >

          <Bell className="h-5 w-5" />


          {unread > 0 && (
            <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold text-white">
              {unread}
            </span>
          )}

        </button>

      </header>


      {/* ======================================================
          MAIN CONTENT
          ====================================================== */}

      <main className="pb-24 md:ml-64 md:pb-8">

        <div className="mx-auto max-w-6xl px-4 py-5 md:px-8 md:py-8">

          {children}

        </div>

      </main>


      {/* ======================================================
          MOBILE BOTTOM NAV
          ====================================================== */}

      <nav className="fixed bottom-0 left-0 z-30 flex w-full items-center justify-around border-t border-slate-200 bg-white/85 px-2 py-1.5 backdrop-blur-xl md:hidden">

        {nav.map((item) => (

          <NavLink
            key={item.to}
            to={item.to}

            data-testid={`mobilenav-${item.to.replace("/", "")}`}

            className={({ isActive }) =>
              cn(
                "relative flex flex-1 flex-col items-center gap-0.5 rounded-lg py-1.5 text-[10px] font-semibold transition",

                isActive
                  ? "text-slate-900"
                  : "text-slate-400"
              )
            }
          >

            {({ isActive }) => (
              <>

                <item.icon
                  className={cn(
                    "h-5 w-5",
                    isActive && "scale-110"
                  )}
                />


                {item.label}


                {item.to === "/notificaciones" &&
                  unread > 0 && (

                    <span className="absolute right-3 top-0 h-1.5 w-1.5 rounded-full bg-red-500" />

                )}

              </>
            )}

          </NavLink>

        ))}

      </nav>


      {/* ======================================================
          MOBILE FAB
          ====================================================== */}

      {(isAdmin || isEmployee) && (
        <button
          onClick={() => setNewTaskOpen(true)}
          data-testid={
            isAdmin
              ? "fab-new-task-btn"
              : "fab-new-personal-task-btn"
          }
          aria-label={
            isAdmin
              ? "Nueva tarea"
              : "Nueva tarea personal"
          }
          title={
            isAdmin
              ? "Nueva tarea"
              : "Nueva tarea personal"
          }
          className="fixed bottom-20 right-5 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500 text-white shadow-lg shadow-emerald-500/40 transition active:scale-90 md:hidden"
        >
          <Plus className="h-7 w-7" />
        </button>
      )}


      {/* ======================================================
          NEW TASK DIALOG
          ====================================================== */}

      <NewTaskDialog
        open={newTaskOpen}
        onOpenChange={setNewTaskOpen}
      />

    </div>
  );
}
